// src/components/ModalsContainer/EditCredentialsSettingModal.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './EditDisplayNameModal.css';

const DEFAULT_OPTIONS = [
  { value: "ENABLED", label: "Enabled" },
  { value: "DISABLED", label: "Disabled" },
];

const EditCredentialsSettingModal = ({
  isOpen,
  settingKey,
  settingLabel,
  currentValue,
  isSaving,
  onSave,
  onCancel,
  settingConfig,
}) => {
  const [selectedValue, setSelectedValue] = useState(currentValue);

  useEffect(() => {
    if (isOpen) {
      setSelectedValue(settingConfig?.inputType === "slider"
        ? Number(currentValue)
        : currentValue);
    }
  }, [isOpen, currentValue, settingConfig]);

  if (!isOpen) return null;

  const inputType = settingConfig?.inputType || "radio";
  const options = settingConfig?.options || DEFAULT_OPTIONS;
  const min = settingConfig?.min;
  const max = settingConfig?.max;
  const unit = settingConfig?.unit;
  const hint = settingConfig?.hint;
  const dangerWarning = settingConfig?.dangerWarning;

  const isSaveDisabled = isSaving || String(selectedValue) === String(currentValue);

  return (
    <div className="edit-modal-overlay">
      <div className="edit-modal-dialog">
        <h2>Edit {settingLabel}</h2>

        {dangerWarning && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.15)',
            border: '1px solid rgba(220, 38, 38, 0.6)',
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '1.25rem',
            color: '#f87171',
            fontSize: '0.9rem',
            lineHeight: '1.5',
          }}>
            ⚠️ <strong>Warning:</strong> {dangerWarning}
          </div>
        )}

        {hint && (
          <p style={{ fontSize: '0.85rem', color: '#9ca3af', marginBottom: '1rem' }}>{hint}</p>
        )}

        {inputType === "slider" ? (
          <div className="edit-modal-slider-group">
            <div className="edit-modal-slider-value">
              {selectedValue}{unit ? ` ${unit}` : ""}
            </div>
            <input
              type="range"
              className="edit-modal-slider"
              min={min}
              max={max}
              value={selectedValue}
              onChange={(e) => setSelectedValue(Number(e.target.value))}
              disabled={isSaving}
            />
            <div className="edit-modal-slider-range">
              <span>{min}{unit ? ` ${unit}` : ""}</span>
              <span>{max}{unit ? ` ${unit}` : ""}</span>
            </div>
          </div>
        ) : inputType === "text" || inputType === "number" ? (
          <div className="edit-modal-text-group">
            <input
              type={inputType}
              className="edit-modal-text-input"
              value={selectedValue ?? ""}
              onChange={(e) => setSelectedValue(inputType === "number" ? Number(e.target.value) : e.target.value)}
              disabled={isSaving}
            />
          </div>
        ) : inputType === "textarea" ? (
          <div className="edit-modal-text-group">
            <textarea
              className="edit-modal-text-input"
              rows={4}
              value={selectedValue ?? ""}
              onChange={(e) => setSelectedValue(e.target.value)}
              disabled={isSaving}
            />
          </div>
        ) : (
          <div className="edit-modal-radio-group">
            {options.map((option) => (
              <label key={option.value} className="edit-modal-radio-label">
                <input
                  type="radio"
                  name={`setting-${settingKey}`}
                  value={option.value}
                  checked={selectedValue === option.value}
                  onChange={() => setSelectedValue(option.value)}
                  disabled={isSaving}
                />
                {option.label}
              </label>
            ))}
          </div>
        )}

        <div className="edit-modal-actions">
          <button
            className="edit-modal-button cancel"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="edit-modal-button save"
            onClick={() => onSave(selectedValue)}
            disabled={isSaveDisabled}
          >
            Save
            {isSaving && <span className="save-spinner"></span>}
          </button>
        </div>
      </div>
    </div>
  );
};

EditCredentialsSettingModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  settingKey: PropTypes.string,
  settingLabel: PropTypes.string,
  currentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSaving: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  settingConfig: PropTypes.shape({
    inputType: PropTypes.oneOf(["radio", "slider", "text", "number", "textarea"]),
    options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
    min: PropTypes.number,
    max: PropTypes.number,
    unit: PropTypes.string,
    hint: PropTypes.string,
    dangerWarning: PropTypes.string,
  }),
};

export default EditCredentialsSettingModal;
